import { useCallback, useEffect, useRef, useState } from 'react';

import { logError } from '@zg-rentals/log-error';
import { DatarouterError } from '@zg-rentals/web-base';

/**
 * The server's message for a 403, which retrying never fixes — e.g. `startOrResume` rejecting anyone but
 * the listing creator (ZRM-17510). `fallback` when that message is empty; undefined for any other error.
 */
const getForbiddenMessage = (error: unknown, fallback: string): string | undefined =>
  DatarouterError.is(error) && error.responseStatus === 403 ? error.message || fallback : undefined;

type InquiryMutation = {
  data?: { inquiryId: string; sessionToken?: string };
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: unknown;
  reset: () => void;
};

export type UsePersonaInquiryFlowParams = {
  /** The result of the surface's own `startOrResume` mutation, so the hook stays endpoint-agnostic. */
  mutation: InquiryMutation;
  /** Must be stable (`useCallback`): the auto-start effect depends on it. */
  start: () => void;
  isStartable: boolean;
  /** Submitted and still being processed: startable, but resuming would hand back a finished inquiry. */
  isProcessing: boolean;
  isStateFetching: boolean;
  refetchState: () => void;
  /** Shown for a 403 whose server message is empty. */
  forbiddenFallback: string;
  logDetails: Record<string, unknown>;
};

/** Start/resume around an embedded Persona inquiry, shared by every surface that hosts one. */
export const usePersonaInquiryFlow = ({
  mutation,
  start,
  isStartable,
  isProcessing,
  isStateFetching,
  refetchState,
  forbiddenFallback,
  logDetails,
}: UsePersonaInquiryFlowParams) => {
  const { data: inquiry, isPending, isError, isSuccess, error, reset } = mutation;

  const [hasAutoStarted, setHasAutoStarted] = useState(false);
  const [hasSdkError, setHasSdkError] = useState(false);
  // A ref, so callers can pass a fresh object each render without recreating `handleSdkError`.
  const logDetailsRef = useRef(logDetails);
  logDetailsRef.current = logDetails;

  // Once per mount, so backing out of Persona's flow doesn't relaunch it. Not while fetching: a
  // cached startable state can outlive a decline, and the server rejects starting from that.
  useEffect(() => {
    if (!isStartable || hasAutoStarted || isStateFetching) return;
    setHasAutoStarted(true);
    start();
  }, [isStartable, hasAutoStarted, isStateFetching, start]);

  const inquiryId = inquiry?.inquiryId;
  // Rejected because the state moved on: its status explains that, a retry never will.
  const isStartRejectedByState = isError && !isStateFetching && !isStartable;
  // The endpoint answers 200 with an error body when it cannot mint an inquiry, so a resolved
  // mutation is not proof of one — and the generated DTO types `inquiryId` as always present.
  const hasFailed = (isError && !isStartRejectedByState) || hasSdkError || (isSuccess && !inquiryId);
  const forbiddenMessage = getForbiddenMessage(error, forbiddenFallback);

  // `meta.invalidates` only runs on success, so a rejection would otherwise leave the stale state up.
  useEffect(() => {
    if (isError && !forbiddenMessage) refetchState();
  }, [isError, forbiddenMessage, refetchState]);

  const retry = useCallback(() => {
    setHasSdkError(false);
    start();
  }, [start]);

  // Separate from cancelling, which would leave the landlord at a neutral button with no idea
  // anything broke. Logged by hand because an SDK callback never reaches the query client.
  const handleSdkError = useCallback(
    (sdkError: unknown) => {
      logError({
        error: sdkError,
        context: { api: 'personaInquiry', details: { ...logDetailsRef.current, inquiryId } },
      });
      setHasSdkError(true);
      reset();
    },
    [inquiryId, reset],
  );

  // Attempted, idle, no inquiry: the landlord backed out of Persona's flow and needs a way in again.
  const canResume = hasAutoStarted && isStartable && !isPending && !hasFailed && !inquiryId && !isProcessing;

  return {
    inquiryId,
    sessionToken: inquiry?.sessionToken,
    // Not just `inquiryId`: reset() clears it a render later, so a broken frame would linger.
    frameInquiryId: hasSdkError ? undefined : inquiryId,
    hasAutoStarted,
    hasFailed,
    hasSdkError,
    forbiddenMessage,
    canResume,
    retry,
    handleSdkError,
  };
};
